import React, { useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useOrderSave } from "../context/OrderSaveContext";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";
import { useStaticOptions } from "../context/StaticOptionsContext";
import ColorPicker, { findStoredColor, normalizeShoeColorOptions } from "../components/form_elements/ColorPicker";

const deriveColorMode = (order) => {
    const h = order.hmeliorder || {};
    if (Number(h.hmeliorder_color_2) > 1 || Number(h.hmeliorder_color_3) > 1) {
        return "Többszínű";
    }
    if (Number(h.hmeliorder_color_1) > 1) {
        return "Egyszínű";
    }
    const leatherColor = (h.hmeliorder_leather_color || "").toString().trim();
    if (!leatherColor) {
        return "";
    }
    return leatherColor.includes(",") ? "Többszínű" : "Egyszínű";
};

/**
 * Egyszínű / többszínű választás és az egyszínű cipő színe.
 * A lista az összes élő szín×bőr opció (nem szűrünk a color_1 jelzőre), kereső névre és kódra.
 */
const EgySzinu = () => {
    const navigate = useStepNavigate();
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const { staticOptions, loading: optionsLoading, error: optionsError } = useStaticOptions();

    const shoe_colors = useMemo(
        () => normalizeShoeColorOptions(staticOptions?.shoe_color_options),
        [staticOptions?.shoe_color_options]
    );

    const [selectedMode, setSelectedMode] = useState(() => deriveColorMode(order));

    const selectedColor = useMemo(
        () => findStoredColor(shoe_colors, order.hmeliorder?.hmeliorder_color_1, order.hmeliorder?.hmeliorder_leather_color),
        [shoe_colors, order.hmeliorder?.hmeliorder_color_1, order.hmeliorder?.hmeliorder_leather_color]
    );

    const showOptions = selectedMode === "Egyszínű";

    const handleAnswer = (answer) => {
        setSelectedMode(answer);
        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: "",
                hmeliorder_color_1: 1,
                hmeliorder_color_2: 1,
                hmeliorder_color_3: 1,
            },
        });
    };

    const handleColorSelect = (color) => {
        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: color.text,
                hmeliorder_color_1: color.id,
                hmeliorder_color_2: 1,
                hmeliorder_color_3: 1,
            },
        });
    };

    const handleBackClick = () => {
        navigate("/SpecialisIgeny");
    };

    const handleNext = async () => {
        if (selectedMode === "Többszínű") {
            navigate("/TobbSzinu");
            return;
        }

        if (selectedMode === "Egyszínű" && selectedColor) {
            const payload = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_leather_color: selectedColor.text,
                    hmeliorder_color_1: selectedColor.id,
                    hmeliorder_color_2: 1,
                    hmeliorder_color_3: 1,
                },
            };

            updateOrder(payload);
            const result = await saveOrder(payload);

            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                navigate("/TuzesSzine");
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        }
    };

    const canProceed = selectedMode === "Többszínű" || (selectedMode === "Egyszínű" && !!selectedColor);

    if (optionsLoading) {
        return (
            <TablerLayout title="Színek kiválasztása">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Betöltés...</span>
                        </div>
                        <h5 className="text-muted">Adatok betöltése...</h5>
                    </div>
                </div>
            </TablerLayout>
        );
    }

    if (optionsError) {
        return (
            <TablerLayout title="Színek kiválasztása">
                <div className="alert alert-danger text-center my-4">
                    <i className="ti ti-alert-triangle me-2"></i>
                    Hiba történt a színek betöltésekor: {optionsError?.message || String(optionsError)}
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Színek kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-header text-center"></div>
                    <div className="card-body">
                        <p className="mb-4 fw-bold fs-5 text-center">
                            Válassza ki, hogy <span className="text-primary">egy</span> vagy <span className="text-primary">többszínű</span> cipőt szeretne!
                        </p>
                        <div className="d-flex flex-wrap gap-4 justify-content-center mb-5">
                            <button
                                type="button"
                                className={`btn w-100 py-4 px-3 shadow-sm rounded-4
                                    ${selectedMode === "Egyszínű" ? "btn-primary" : "btn-outline-primary "}`}
                                style={{
                                    fontSize: 22,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    minHeight: 70,
                                    minWidth: 250,
                                }}
                                onClick={() => handleAnswer("Egyszínű")}
                            >
                                Egyszínű
                            </button>
                            <button
                                type="button"
                                className={`btn w-100 py-4 px-3 shadow-sm rounded-4
                                    ${selectedMode === "Többszínű" ? "btn-primary" : "btn-outline-primary "}`}
                                style={{
                                    fontSize: 22,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    minHeight: 70,
                                    minWidth: 250,
                                }}
                                onClick={() => handleAnswer("Többszínű")}
                            >
                                Többszínű
                            </button>
                        </div>

                        {showOptions && (
                            <div className="row justify-content-center">
                                <div className="col-md-12">
                                    {shoe_colors.length === 0 ? (
                                        <div className="alert alert-warning text-center mb-0">Nincs választható szín.</div>
                                    ) : (
                                        <ColorPicker
                                            colors={shoe_colors}
                                            selectedId={selectedColor?.id ?? null}
                                            onSelect={handleColorSelect}
                                            autoFocus
                                            maxHeight={360}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                        <StepperNavButton side="right" label="Tovább" onClick={handleNext} disabled={!canProceed} />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default EgySzinu;
